import type { ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/auth";
import { isEmbedded } from "@/features/embed/frame";
import { t } from "@/i18n";

/** `/embed/ikas`'in App.tsx'teki tam yolu (App.tsx dokunulmaz kisiti —
 * bkz. asagida RequireTenant). */
const EMBED_IKAS_PATH = "/embed/ikas";

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-sm text-muted">
      {t("common.loading")}
    </div>
  );
}

/** Oturum acilmis olmali (tenant sarti yok) — ör. /tenant-sec. */
export function RequireAuth({ children }: { children: ReactElement }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous") return <Navigate to="/giris" replace state={{ from: location }} />;
  return children;
}

/**
 * Oturum + aktif tenant secilmis olmali (sozlesme §2 "tenant_user").
 *
 * İNCELEME BULGUSU (kritik): `/embed/ikas` App.tsx'te bu guard ile sarili
 * (App.tsx dokunulmaz kisiti — ayri bir embed-ozel guard'a tasinamiyor).
 * ikas admin'in çapraz-site iframe'i içinde ana oturum çerezi (SameSite=Lax,
 * bkz. backend/app/core/auth.py) tarayici tarafindan HİÇ gönderilmez —
 * `useAuth()` bu yuzden burada HER ZAMAN "anonymous" görür. Bu daldan
 * cikilmadan once (asagidaki gibi) sadece /giris'e yonlendirilseydi,
 * `EmbedIkas` hic MOUNT OLMADAN kullanici iframe İÇİNDE tam boy bir giris
 * formuna dusuyordu — kendi bearer akisi (useEmbedSession → POST
 * /embedded-session) hic baslamiyordu. Çözüm: anonim + iframe ICINDE +
 * tam olarak `/embed/ikas` ise yönlendirme YAPILMAZ, `children`
 * (EmbedIkas) dogrudan render edilir — o, kendi "loading/unauthorized/
 * error/ready" durumlarini (bearer akisi 401 dahil) BAĞIMSIZ yönetir ve
 * dogru "tam ekranda giris yap" dusmesini gosterir. App.tsx'in
 * dokunulmazlik kisiti kalkinca bu dal silinip `/embed/ikas` kendi
 * guard'siz/embed-ozel bir guard'a tasinabilir.
 */
export function RequireTenant({ children }: { children: ReactElement }) {
  const { status, principal } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous") {
    // .replace: react-router "/embed/ikas/" (sondaki "/") icin de eslesir,
    // burada da AYNI sekilde ele alinmali.
    if (location.pathname.replace(/\/$/, "") === EMBED_IKAS_PATH && isEmbedded()) return children;
    return <Navigate to="/giris" replace state={{ from: location }} />;
  }
  if (!principal?.active_tenant) return <Navigate to="/tenant-sec" replace />;
  return children;
}

/** Oturum + aktif tenant + rol tenant_admin (ya da vdd_super). */
export function RequireTenantAdmin({ children }: { children: ReactElement }) {
  const { status, principal } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous") return <Navigate to="/giris" replace state={{ from: location }} />;
  if (!principal?.active_tenant) return <Navigate to="/tenant-sec" replace />;
  const isAdmin = principal.role === "tenant_admin" || principal.user.is_super;
  if (!isAdmin) return <Navigate to="/ozet" replace />;
  return children;
}

/** Yalniz vdd_super (tenant sarti yok — /admin tenant secilmeden de erisilir). */
export function RequireSuper({ children }: { children: ReactElement }) {
  const { status, principal } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner />;
  if (status === "anonymous") return <Navigate to="/giris" replace state={{ from: location }} />;
  if (!principal?.user.is_super) return <Navigate to="/ozet" replace />;
  return children;
}
